import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { toggle_timeline_events_task_path } from "../routes"

export default class extends Controller {
  static values = {
    buildingComplexId: String,
    filterActive: Boolean,
    taskId: Number,
    timelineShowEvents: Boolean
  }
  static params = ["buildingComplexId"]
  static targets = ["filterContainer", "modelSearch", "filterToggleButton", "timelineToggle"]
  static outlets = ["task-breadcrumbs"]

  connect() {
    this.notifyFilterState()
    this.initializeTimelineToggle()
  }

  handleFormSubmit(event) {
    // Check if the form submission was for the search form
    if (event.target.id === 'model_searchform') {
      // Check if there are any filled form fields to determine if filter will be active
      const formData = new FormData(event.target)
      let hasActiveFilters = false
      const entries = []

      // Check all form fields except ignored ones
      const ignoredFields = ['clear', 'building_complex_id', 'scope', 'authenticity_token', 'commit']
      for (const [key, value] of formData.entries()) {
        entries.push({ key, value })
      }

      // Special handling: if the clear flag is set explicitly, treat as no active filters
      if (formData.get('clear') === '1') {
        hasActiveFilters = false
      } else {
        for (const entry of entries) {
          const { key, value } = entry
          if (ignoredFields.includes(key)) {
            continue
          }

          const normalizedValue = typeof value === 'string' ? value.trim() : value
          if (normalizedValue === '' || normalizedValue === '0' || normalizedValue === null) {
            continue
          }

          hasActiveFilters = true
          break
        }
      }
      console.debug('[task_controller] handleFormSubmit entries', entries, 'hasActiveFilters', hasActiveFilters)
      
      // Update icon based on whether filters will be active
      this.filterActiveValue = hasActiveFilters
      this.closeFilter()
    }
  }

  closeFilter() {
    if (this.hasFilterContainerTarget) {
      this.filterContainerTarget.classList.remove('show')
      this.notifyFilterState()
    }
  }

  filterActiveValueChanged() {
    this.notifyFilterState()
  }





  clearForm() {
    document.getElementById("model_searchform").querySelector("#clear").value = 1;
    document.getElementById("model_searchform").setAttribute("data-turbo", "false");
    document.getElementById("model_searchform").requestSubmit();
  }

  toggleFilter() {
    if (this.hasFilterContainerTarget) {
      // Toggle the show class for smooth animation
      this.filterContainerTarget.classList.toggle('show')
      
      // Notify breadcrumbs about current filter state
      this.notifyFilterState()
    }
  }

  updateFilterButtonState(isVisible) {
    // Use outlet to update the filter button state in breadcrumbs controller
    if (this.hasTaskBreadcrumbsOutlet) {
      this.taskBreadcrumbsOutlet.updateFilterButtonState(isVisible)
    }
  }

  updateFilterIcon(isActive) {
    // Use outlet to update the filter icon in breadcrumbs controller
    if (this.hasTaskBreadcrumbsOutlet) {
      this.taskBreadcrumbsOutlet.updateFilterIcon(isActive)
    }
  }

  updateBreadcrumbs(event) {
    // Update breadcrumbs when building complex changes
    const buildingComplexId = event.currentTarget.dataset.buildingComplexId
    if (this.hasTaskBreadcrumbsOutlet) {
      this.taskBreadcrumbsOutlet.buildingComplexIdValue = buildingComplexId
    }
  }

  notifyFilterState() {
    if (!this.hasTaskBreadcrumbsOutlet) return

    const isVisible = this.hasFilterContainerTarget ? this.filterContainerTarget.classList.contains('show') : false
    const isActive = !!this.filterActiveValue

    console.debug('[task_controller] notifyFilterState', { isVisible, isActive })

    this.updateFilterButtonState(isVisible)
    this.updateFilterIcon(isActive)
  }

  taskBreadcrumbsOutletConnected() {
    console.debug('[task_controller] taskBreadcrumbsOutletConnected')
    this.notifyFilterState()
  }

  initializeTimelineToggle() {
    if (!this.hasTimelineToggleTarget) return

    const defaultState = this.hasTimelineShowEventsValue ? this.timelineShowEventsValue : false
    this.timelineToggleTarget.checked = defaultState
  }

  toggleTimelineEvents(event) {
    const showEvents = event.currentTarget.checked
    this.timelineShowEventsValue = showEvents
    const taskId = this.resolveTaskId(event)
    if (!taskId) {
      console.warn('[task_controller] Missing taskId for timeline toggle')
      return
    }

    const url = toggle_timeline_events_task_path(taskId)

    const formData = new FormData()
    formData.append('show_events', showEvents ? '1' : '0')

    fetch(url, {
      method: 'PATCH',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': this.csrfToken
      },
      body: formData
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.text()
      })
      .then(html => {
        if (html && html.trim().length > 0) {
          Turbo.renderStreamMessage(html)
        }
      })
      .catch(error => {
        console.error('[task_controller] toggleTimelineEvents failed', error)
        event.currentTarget.checked = !showEvents
      })
  }

  get csrfToken() {
    const element = document.querySelector('meta[name="csrf-token"]')
    return element ? element.content : ''
  }

  resolveTaskId(event) {
    if (this.hasTaskIdValue) {
      return this.taskIdValue
    }

    if (event?.params?.taskId) {
      return event.params.taskId
    }

    const datasetValue = event?.currentTarget?.dataset?.taskId
    return datasetValue ? Number(datasetValue) : null
  }
}
